import "@/lib/i18n";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { type QueryClient } from "@tanstack/react-query";
import { type AuthSlice } from "@njbsoft/store";
import { useMediaQuery } from "@njbsoft/form-ui";
import { ErrorBoundary } from "@/components/error-boundary";
import { useThemeSync } from "@/hooks/use-theme-sync";
import { useAccentSync } from "@/hooks/use-accent-sync";
import { useLocaleSync } from "@/hooks/use-locale-sync";

import { Toaster } from "sonner";

interface RouterContext {
  queryClient: QueryClient;
  auth: AuthSlice;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

/** Tailwind's default `md` breakpoint, where the shell's sidebar goes static. */
const SHELL_MD_QUERY = "(min-width: 48rem)";

/**
 * The Query and router devtools toggles, from `md` only. Below `md` the bottom
 * corners are a phone's submit bar and drawer footers and top-left is the menu
 * trigger, and a toggle there takes their taps, so neither is mounted at all —
 * their own styles are unlayered and would beat any utility class that tried
 * to hide them. From `md` the router toggle sits top-left, over the sidebar
 * brand, because every other corner holds a control: bottom-left the Query
 * toggle, bottom-right the drawer footers (task/device create buttons),
 * top-right every right-anchored drawer's close control.
 */
function DevtoolsFromMd() {
  const fromMd = useMediaQuery(SHELL_MD_QUERY, false);
  if (!fromMd) return null;
  return (
    <>
      <ReactQueryDevtools buttonPosition="bottom-left" />
      <TanStackRouterDevtools position="top-left" />
    </>
  );
}

function RootComponent() {
  useThemeSync();
  useAccentSync();
  useLocaleSync();

  return (
    <>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
      {/* At or below sonner's 600 px breakpoint a toast spans the screen, so
          the stack starts under the shell's 56 px top bar plus sonner's own
          16 px gap — never over the menu trigger. */}
      <Toaster richColors position="top-right" mobileOffset={{ top: 72 }} />
      {import.meta.env.DEV && <DevtoolsFromMd />}
    </>
  );
}
